import React from 'react';
import { MessageSquare, GitBranch, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/core/utils';
import type { AIConversationSummary } from '../types';

const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

interface MobileChatHistoryProps {
  conversations: AIConversationSummary[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (e: React.MouseEvent, id: string) => void;
  className?: string;
}

/**
 * History view for mobile AI chat.
 * Shows conversation list with delete and empty state handling.
 */
export const MobileChatHistory: React.FC<MobileChatHistoryProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  className,
}) => {
  return (
    <div className={cn(
      'flex-1 overflow-y-auto min-h-0',
      className
    )}>
      {conversations.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-6">
          <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
          <p className="text-base">No chat history yet</p>
          <p className="text-sm mt-1">Start a conversation to see it here</p>
        </div>
      ) : (
        <div className="p-3 space-y-2">
          {conversations.map((conv) => {
            const isActive = conv.id === activeConversationId;
            return (
              <div
                key={conv.id}
                onClick={() => onSelectConversation(conv.id)}
                className={cn(
                  'group flex items-center gap-3 p-3 rounded-lg cursor-pointer',
                  'active:bg-muted/80',
                  isActive && 'bg-primary/10 border-l-2 border-primary'
                )}
              >
                {conv.forkedFromConversationId ? (
                  <GitBranch className={cn(
                    'h-5 w-5 shrink-0',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )} />
                ) : (
                  <MessageSquare className={cn(
                    'h-5 w-5 shrink-0',
                    isActive ? 'text-primary' : 'text-muted-foreground'
                  )} />
                )}
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm truncate', isActive && 'font-medium')}>
                    {conv.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {conv.forkedFromConversationId && (
                      <span className="text-primary/70">Branch · </span>
                    )}
                    {conv.messageCount} messages · {formatRelativeTime(conv.updatedAt)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 opacity-40"
                  onClick={(e) => onDeleteConversation(e, conv.id)}
                >
                  <Trash2 className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
